import { api } from './api';

export interface RequiredForm {
  wins: number;
  draws: number;
  losses: number;
  pointsPerGame: number;
  description: string;
}

export interface StrategicMatch {
  opponent: string;
  opponentLogoPath: string;
  opponentPosition: number;
  opponentPoints: number;
  difficulty: 'FAVORABLE' | 'COMPETITIVE' | 'CHALLENGING';
  isRequiredForTarget: boolean;
}

export interface StrategicReasoning {
  requiredWins: number;
  totalMatches: number;
  targetPosition: number;
  upcomingMatches: StrategicMatch[];
  externalDependencies?: string[];
}

export interface ScenarioResult {
  position: number;
  points: number;
  requiredForm: RequiredForm;
  probability?: number;
  contextualLabel: string;
  dependencies?: string[];
  strategicReasoning?: StrategicReasoning;
}

export interface PositionScenarios {
  best: ScenarioResult;
  mostLikely: ScenarioResult;
}

export interface MatchImpact {
  win: {
    position: number;
    change: number;
  };
  draw: {
    position: number;
    change: number;
  };
  loss: {
    position: number;
    change: number;
  };
}

export interface CriticalMatch {
  matchId: string;
  opponent: string;
  dateTime: Date;
  location: string;
  isDirectCompetitor: boolean;
  impact: MatchImpact;
}

export interface TeamDependency {
  teamName: string;
  requiredResults: string;
  matches: string[];
}

export interface PositionDependency {
  controlsDestiny: boolean;
  dependencies: TeamDependency[];
}

export interface TeamAnalysis {
  teamId: string;
  teamName: string;
  leagueId: string;
  currentPosition: number;
  scenarios: PositionScenarios;
  criticalMatches: CriticalMatch[];
  dependencies: PositionDependency;
  historicalAccuracy?: {
    totalPredictions: number;
    accuratePredictions: number;
    accuracyPercentage: number;
    averagePositionError: number;
  };
}

export interface TeamAnalysisSummary {
  teamId: string;
  teamName: string;
  currentPosition: number;
  bestPosition: number;
  likelyPosition: number;
  controlsDestiny: boolean;
  isUserTeam: boolean;
}

export interface LeagueAnalysis {
  leagueId: string;
  leagueName: string;
  teams: TeamAnalysisSummary[];
}

export const positionAnalysisService = {
  /**
   * Get comprehensive analysis for a team
   */
  async getTeamAnalysis(teamId: string, leagueId: string): Promise<TeamAnalysis> {
    const response = await api.get(`/analysis/teams/${teamId}/position-analysis`, {
      params: { leagueId }
    });
    return response.data;
  },

  /**
   * Get analysis for all tracked teams in a league
   */
  async getLeagueAnalysis(leagueId: string, teamIds?: string[]): Promise<LeagueAnalysis> {
    const params: any = {};
    if (teamIds && teamIds.length > 0) {
      params.teamIds = teamIds.join(',');
    }
    
    const response = await api.get(`/analysis/leagues/${leagueId}/position-analysis`, {
      params
    });
    return response.data;
  },

  /**
   * Get team scenarios only
   */
  async getTeamScenarios(teamId: string, leagueId: string): Promise<{
    teamId: string;
    leagueId: string;
    currentPosition: number;
    scenarios: PositionScenarios;
    dependencies: PositionDependency;
  }> {
    const response = await api.get(`/analysis/teams/${teamId}/scenarios`, {
      params: { leagueId }
    });
    return response.data;
  },

  /**
   * Get critical matches for a team
   */
  async getCriticalMatches(teamId: string, leagueId: string): Promise<{
    teamId: string;
    leagueId: string;
    criticalMatches: CriticalMatch[];
  }> {
    const response = await api.get(`/analysis/teams/${teamId}/critical-matches`, {
      params: { leagueId }
    });
    return response.data;
  }
};
